import { useState, type FormEvent } from 'react'
import { servicesApi } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ChipGroup } from './ChipGroup'
import { useAuth } from '../auth/AuthContext'
import {
  hasTutorArrangement,
  isInstitutionCategory,
  OFFER_CATEGORY_BY_ENTITY,
  OFFER_DETAIL_FIELDS,
} from '../utils/institutionOffer'
import { ErrorMessage } from './ErrorMessage'
import { useTaxonomy } from '../taxonomy/TaxonomyContext'
import { toStringArray } from '../utils/taxonomy'
import type { NeedCategory, ProviderServiceResponse } from '../types/api'

export function ServiceForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial?: ProviderServiceResponse
  onCancel: () => void
  onSaved: () => void
}) {
  // Items and levels used to be free-text comma lists, which could only match the learner side by
  // luck: a tutor typing "Maths" or "Piano / Keyboard" wrote a value no need can ever match, and
  // the server rejected it outright. They are pickers over the shared taxonomy now.
  const {
    categories,
    labelOf,
    itemsOf,
    levelsOf,
    categoryOf,
    detailOptionsOf,
    common,
    loading,
    error: taxonomyError,
  } = useTaxonomy()

  const { profile } = useAuth()
  // An institution has exactly one category, decided by what it is. Opening on Academics meant a
  // school was shown the whole tutor form and had to know to change a dropdown first.
  const fixedCategory = OFFER_CATEGORY_BY_ENTITY[profile?.entityKind ?? '']

  const [category, setCategory] = useState<NeedCategory>(
    initial?.category ?? fixedCategory ?? 'ACADEMICS',
  )
  const [items, setItems] = useState<string[]>(toStringArray(initial?.items))
  // Entrepreneurship only: "What I Have" (a different vocabulary from "What I Need"), translated
  // server-side into offered items by ProviderServiceService - see Taxonomy.entrepreneurOfferedItems.
  const [haves, setHaves] = useState<string[]>(
    toStringArray((initial?.detail as Record<string, unknown> | undefined)?.haves),
  )
  const [levels, setLevels] = useState<string[]>(toStringArray(initial?.levels))
  const [modes, setModes] = useState<string[]>(toStringArray(initial?.modes))
  const [days, setDays] = useState<string[]>(toStringArray(initial?.days))
  const [timings, setTimings] = useState<string[]>(toStringArray(initial?.timings))
  const [boards, setBoards] = useState<string[]>(toStringArray(initial?.boards))
  const [classes, setClasses] = useState<string[]>(toStringArray(initial?.classes))
  const [teachingLanguages, setTeachingLanguages] = useState<string[]>(
    toStringArray(initial?.teachingLanguages),
  )
  const [feeBand, setFeeBand] = useState<string>(initial?.feeBand ?? '')
  /** An institution's own answers - degree, positions, fee. Empty for a tutor. */
  const [detail, setDetail] = useState<Record<string, string>>(
    (initial?.detail as Record<string, string> | undefined) ?? {},
  )
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary ?? false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const activeCategory = categoryOf(category)
  const allowedLevels = levelsOf(category)
  // Boards and classes are the category's own filters, so they only appear where it declares
  // them. Sending either to a category without them is rejected as *_not_applicable.
  const allowedBoards = detailOptionsOf(category, 'board')
  const allowedClasses = detailOptionsOf(category, 'class')
  const isInstitution = isInstitutionCategory(category)
  const offerFields = OFFER_DETAIL_FIELDS[category] ?? []
  const isEntrepreneurship = category === 'ENTREPRENEURSHIP'

  function toggle(list: string[], set: (v: string[]) => void, value: string) {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (isEntrepreneurship ? haves.length === 0 : items.length === 0) {
      setError(
        isEntrepreneurship
          ? 'Choose at least one thing you can offer another entrepreneur'
          : `Choose at least one option under "${activeCategory?.itemsLabel ?? 'Subjects / skills'}" - `
            + 'learners are matched to you by category and subject, so this is required.',
      )
      return
    }
    // Only a tutor is asked how they teach, so only a tutor can fail to answer it.
    if (hasTutorArrangement(category) && modes.length === 0) {
      setError('Pick at least one teaching mode')
      return
    }
    setSubmitting(true)
    try {
      const noArrangement = !hasTutorArrangement(category)
      await servicesApi.upsert({
        category,
        // Entrepreneurship: the server derives items from detail.haves (see
        // ProviderServiceService) and ignores whatever is sent here, so this is never the real
        // picker for it - haves is, below.
        items: isEntrepreneurship ? [] : items,
        // A class-graded category (Academics, Competitive Exams) declares no levels, and sending
        // any is rejected as levels_not_applicable.
        levels: noArrangement || !allowedLevels ? [] : levels,
        // Sent empty rather than omitted when not asked for, so editing a row that once had them
        // clears what is no longer asked for instead of leaving it behind.
        modes: noArrangement ? [] : modes,
        days: noArrangement ? [] : days,
        timings: noArrangement ? [] : timings,
        boards: allowedBoards.length ? boards : [],
        // An institution's classes are its items; only a tutor has a separate class filter.
        classes: hasTutorArrangement(category) && allowedClasses.length ? classes : [],
        teachingLanguages: noArrangement ? [] : teachingLanguages,
        feeBand: noArrangement ? undefined : feeBand || undefined,
        // Blank answers dropped: the server treats a missing key as unanswered and an empty
        // string as a value outside the vocabulary. haves rides along here, not as its own field -
        // see ProviderServiceService, which reads it off detail.
        detail: {
          ...Object.fromEntries(Object.entries(detail).filter(([, v]) => v)),
          ...(isEntrepreneurship ? { haves } : {}),
        },
        isPrimary,
      })
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.details?.length ? `${err.message}: ${err.details.join(', ')}` : err.message)
      } else {
        setError('Failed to save')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="card-pad text-sm text-ink-500">Loading options...</div>
  }
  if (taxonomyError) {
    return (
      <div className="card-pad space-y-3">
        <ErrorMessage message={`Could not load the options list: ${taxonomyError}`} />
        <button type="button" onClick={onCancel} className="btn-ghost">Close</button>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="card-pad space-y-3">
      {/* Not shown to a school, college or company: their category is fixed, so there is no pick to explain. */}
      {!fixedCategory && (
        <p className="rounded-xl border border-sun-400/50 bg-sun-400/10 px-3 py-2 text-xs text-amber-900">
          Category and {isEntrepreneurship ? 'What I provide' : activeCategory?.itemsLabel ?? 'Subjects / skills'} are
          both required - the map only shows you to learners looking for exactly what you pick here.
        </p>
      )}

      {/* Hidden entirely for an institution: there is nothing to choose, and a picker offering a
          school "Music" is a question it should never be asked. */}
      {!fixedCategory && (
      <div className="category-panel">
        <span className="label label-required">Category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.name}
              type="button"
              disabled={!!initial}
              onClick={() => {
                // Unlike <select>'s onChange, a button fires on every click, including a re-click
                // of the category already active - skip the reset then, or it would wipe whatever
                // was just filled in below for no actual change.
                if (c.name === category) return
                setCategory(c.name)
                // All four are category-scoped; keeping them would submit values this category
                // disallows. Modes, days, timings and fee are category-independent, so they stay.
                setItems([])
                setLevels([])
                setBoards([])
                setClasses([])
                setDetail({})
              }}
              className={`chip ${category === c.name ? 'chip-active-category' : ''} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {labelOf(c.name)}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Everything below is category-scoped and changes out from under you when you pick a
          different category above - the line makes that boundary visible instead of letting the
          whole form look like it just silently changed shape. */}
      {!fixedCategory && <div className="border-t border-ink-200" />}

      {isEntrepreneurship ? (
        <ChipGroup
          label="What I provide"
          required
          options={detailOptionsOf(category, 'haves')}
          values={haves}
          onToggle={(v) => toggle(haves, setHaves, v)}
        />
      ) : (
        <ChipGroup
          label={activeCategory?.itemsLabel ?? 'Subjects / skills'}
          required
          options={itemsOf(category)}
          values={items}
          onToggle={(v) => toggle(items, setItems, v)}
        />
      )}

      {!hasTutorArrangement(category) ? null : allowedLevels ? (
        <ChipGroup
          label="Levels taught"
          options={allowedLevels}
          values={levels}
          onToggle={(v) => toggle(levels, setLevels, v)}
        />
      ) : (
        <p className="text-sm text-ink-500">
          This category is graded by class rather than by level, so there is nothing to pick here.
        </p>
      )}

      {/* An institution's own questions. Which ones belong here is in OFFER_DETAIL_FIELDS; the
          options themselves come from the server's taxonomy, so nothing offered here can be a
          value it would reject. */}
      {isInstitution && offerFields.length > 0 && (
        <div className="space-y-3 border-t border-ink-200 pt-3">
          {offerFields.map((f) => (
            <ChipGroup
              key={f.key}
              label={f.label}
              options={detailOptionsOf(category, f.key)}
              value={detail[f.key] ?? ''}
              onChange={(v) =>
                setDetail((d) => ({ ...d, [f.key]: v === d[f.key] ? '' : v }))
              }
            />
          ))}
        </div>
      )}

      {(allowedBoards.length > 0 || allowedClasses.length > 0) && (
        <div className="space-y-3 border-t border-ink-200 pt-3">
          <p className="text-sm text-ink-500">For your school subjects and exam prep: boards and classes.</p>
          {allowedBoards.length > 0 && (
            <ChipGroup
              label="Boards you teach"
              options={allowedBoards}
              values={boards}
              onToggle={(v) => toggle(boards, setBoards, v)}
            />
          )}
          {allowedClasses.length > 0 && (
            <ChipGroup
              label="Classes"
              options={allowedClasses}
              values={classes}
              onToggle={(v) => toggle(classes, setClasses, v)}
            />
          )}
        </div>
      )}

      {/* How, when and in what language you teach, and at what hourly rate. None of it applies to
          an institution (a school admits a class for a year, a company is not teaching at all) or
          to Entrepreneurship (a match there is a co-founder, mentor or investor, not a tutoring
          arrangement) - see hasTutorArrangement. */}
      {hasTutorArrangement(category) && (
      <div className="space-y-3 border-t border-ink-200 pt-3">
        <p className="text-sm text-ink-500">Choose how you teach.</p>
        <ChipGroup
          label="Teaching modes"
          required
          options={common.mode}
          values={modes}
          onToggle={(v) => toggle(modes, setModes, v)}
        />
        <ChipGroup
          label="Available days"
          options={common.day}
          values={days}
          onToggle={(v) => toggle(days, setDays, v)}
        />
        <ChipGroup
          label="Time bands"
          options={common.timing}
          values={timings}
          onToggle={(v) => toggle(timings, setTimings, v)}
        />
        <ChipGroup
          label="Languages you teach in"
          options={common.teaching_language}
          values={teachingLanguages}
          onToggle={(v) => toggle(teachingLanguages, setTeachingLanguages, v)}
        />
        <div>
          <ChipGroup
            label="Fee range"
            options={common.budget_band}
            value={feeBand}
            onChange={(v) => setFeeBand(v === feeBand ? '' : v)}
          />
          <p className="mt-1 text-xs text-ink-400">
            The same bands learners pick from, so your fee can be compared with their budget.
          </p>
        </div>
      </div>
      )}

      <label className="flex items-center gap-2 text-sm text-ink-600">
        <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
        Primary service
      </label>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={submitting} className="btn-sun">{submitting ? 'Saving...' : 'Save'}</button>
      </div>
    </form>
  )
}
